import ctl from '@netlify/classnames-template-literals'
import { motion, PanInfo } from 'motion/react'
import { ReactNode, Ref, useCallback, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TestingBanner } from '../../core/testing-banner'
import { JobiLoader } from '../../rapid-ui'
import { CampaignAccess } from '../../views/dashboard/campaign-access'
import { EditorAccordionRef, EditorShellAccordion } from './accordion'
import { EditorShellHeading } from './heading'
import { SaveStatusButton } from './save-status-button'

export type EditorShellRef = {
	moveToDetailsStep: () => void
	moveToEditStep: () => void
	moveToReviewStep: () => void
}

export interface EditorShellProps {
	campaignId?: string
	defaultPanelId?: 'details' | 'edit' | 'review'
	enabledPanelIds?: Array<'details' | 'edit' | 'review'>

	isLoading?: boolean

	detailsPanel: ReactNode
	editPanel: ReactNode
	reviewPanel: ReactNode
	ref: Ref<EditorShellRef>

	simulators?: ReactNode
}

const DEFAULT_SIDEBAR_OFFSET = 630
const MIN_SIDEBAR_OFFSET = 320

/**
 * Functional UI wrapper for the editor. Business logic is handled in the editor component.
 */
export function EditorShell({
	campaignId,
	defaultPanelId = 'details',
	enabledPanelIds = [],
	isLoading = false,
	detailsPanel,
	editPanel,
	reviewPanel,
	ref,
	simulators,
}: EditorShellProps) {
	const [sidebarOffset, setSidebarOffset] = useState(DEFAULT_SIDEBAR_OFFSET)

	const handleDrag = useCallback((_: unknown, info: PanInfo) => {
		setSidebarOffset(() => Math.max(MIN_SIDEBAR_OFFSET, info.point.x))
	}, [setSidebarOffset])

	const handleDoubleClick = useCallback(() => {
		setSidebarOffset(DEFAULT_SIDEBAR_OFFSET)
	}, [setSidebarOffset])

	const { t } = useTranslation('common')
	const accordionRef = useRef<EditorAccordionRef>(null)

	useImperativeHandle(ref, () => ({
		moveToDetailsStep: () => {
			accordionRef.current?.moveToDetailsStep()
		},
		moveToEditStep: () => {
			accordionRef.current?.moveToEditStep()
		},
		moveToReviewStep: () => {
			accordionRef.current?.moveToReviewStep()
		},
	}))

	return (
		<>
			<TestingBanner />
			<div
				className={'grid relative'}
				style={{
					gridTemplateColumns: `${sidebarOffset}px 1fr`,
					height: '100vh',
				}}
			>
				<div className="bg-white relative h-full grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr] min-h-0">
					{!isLoading && (
						<>
							<EditorShellHeading
								rightSide={
									<div className="flex items-center gap-3">
										<SaveStatusButton />
										{campaignId && (
											<CampaignAccess campaignId={campaignId} prefetch source="editor" />
										)}
									</div>
								}
							/>
							<EditorShellAccordion
								enabledPanelIds={enabledPanelIds}
								campaignId={campaignId}
								ref={accordionRef}
								panels={[{
									id: 'details',
									title: t('editor.shell.detailsStep'),
									render: () => detailsPanel,
								}, {
									id: 'edit',
									title: t('editor.shell.editStep'),
									render: () => editPanel,
								}, {
									id: 'review',
									title: t('editor.shell.reviewStep'),
									render: () => reviewPanel,
								}]}
								defaultPanelId={defaultPanelId}
							/>
						</>
					)}
					<div className="absolute inset-y-0 left-full w-1 bg-gradient-to-r from-black/5 to-transparent z-10" />
					<div className="absolute inset-y-0 left-full w4 bg-gradient-to-r from-black/10 to-transparent z-10" />
				</div>
				<div
					className={ctl(`
						h-full relative overflow-y-auto
					`)}
					data-editor-scroll-container="true"
				>
					{/* No background here means the simulators get the default document backdrop from CommonUiProvider. */}
					<div>
						{!isLoading && <>{simulators}</>}
					</div>
				</div>
			</div>

			<motion.div
				className="absolute inset-y-0 -left-2 w-4 cursor-col-resize z-20 bg-transparent select-none touch-none"
				style={{
					translateX: `${sidebarOffset}px`,
				}}
				transition={{
					type: 'tween',
					ease: 'linear',
				}}
				onPan={handleDrag}
				onDoubleClick={handleDoubleClick}
			>
				<motion.div className="absolute inset-y-0 w-1 left-1/2 -translate-x-1/2 " />
			</motion.div>

			{/* <AnimatePresence> */}
			{isLoading && (
				<>
					<motion.div
						initial={{
							opacity: 0,
							backdropFilter: 'blur(0px)',
							background: 'rgba(255,255,255,0)',
						}}
						animate={{
							opacity: 1,
							background: 'rgba(255,255,255,0.5)',
							backdropFilter: 'blur(10px)',
							transition: { duration: 2, ease: 'easeInOut', delay: 1 },
						}}
						exit={{
							opacity: 0,
							background: 'rgba(255, 255, 255, 0.5)',
							backdropFilter: 'blur(0px)',
							transition: { duration: 0.3, ease: 'easeInOut' },
						}}
						className="absolute inset-0 z-50"
					>
					</motion.div>
					<div className="absolute inset-0 flex items-center justify-center flex-col gap-4 z-60 pt-16">
						<motion.div
							initial={{ opacity: 0 }}
							animate={{
								opacity: 1,
								scale: 1,
								transition: { duration: 0.4, ease: 'easeInOut' },
							}}
							exit={{ opacity: 0, scale: 0.9 }}
						>
							<JobiLoader />
						</motion.div>
						<motion.div
							initial={{ opacity: 0, y: 4, filter: 'blur(3px)' }}
							animate={{
								opacity: 1,
								y: 0,
								filter: 'blur(0px)',
								transition: { duration: 0.4, ease: 'easeInOut', delay: 2 },
							}}
							exit={{
								opacity: 0,
								y: 4,
								filter: 'blur(3px)',
							}}
							className="text-sm text-grey-700 tracking-wide mt-16"
						>
							{t('editor.shell.loading')}
						</motion.div>
						<motion.div
							initial={{ opacity: 0, y: 4, filter: 'blur(3px)' }}
							animate={{
								opacity: 1,
								y: 0,
								filter: 'blur(0px)',
								transition: { duration: 0.4, ease: 'easeInOut', delay: 10 },
							}}
							exit={{ opacity: 0, y: 4, filter: 'blur(3px)' }}
							className="text-xs text-grey-400 tracking-normal max-w-[300px] text-center"
						>
							{t('editor.shell.loadingDelay')}{' '}
							<span
								className="text-brand-500 cursor-pointer underline"
								onClick={() => window.location.reload()}
							>
								{t('editor.shell.refreshPage')}
							</span>.
						</motion.div>
					</div>
				</>
			)}
			{/* </AnimatePresence> */}
		</>
	)
}
